import React from 'react';

import { CheckCircleIcon } from '@phosphor-icons/react';
import {
	AlertCircle,
	CheckCircle,
	Clock,
	ClockIcon,
	RotateCcw,
	XCircle,
	XCircleIcon,
} from 'lucide-react';

import { useRetryStatementMutation } from '../api';

import Loader from './Loader';
import { hasCreditSettlementFailure } from './processingStatus';

import type { ProcessingLogs } from '../api';

interface ProcessingLogsProps {
	logsData?: ProcessingLogs;
	isLoading?: boolean;
	error?: any;
	onRetry?: () => void;
}

interface ProcessingLogEntry {
	id: string;
	step_name: string;
	status: string;
	message: string;
	error_message?: string;
	created_at: string;
	started_at?: string;
	completed_at?: string;
	duration_seconds?: number;
	step_data?: any;
}

const getStatusIcon = (status: string) => {
	switch (status.toLowerCase()) {
		case 'completed':
			return <CheckCircle className="w-5 h-5 text-green-500" />;
		case 'failed':
			return <XCircle className="w-5 h-5 text-red-500" />;
		case 'processing':
			return <Clock className="w-5 h-5 text-blue-500" />;
		case 'uploaded':
			return <CheckCircle className="w-5 h-5 text-gray-500" />;
		default:
			return <AlertCircle className="w-5 h-5 text-yellow-500" />;
	}
};

const getStatusColor = (status: string) => {
	switch (status.toLowerCase()) {
		case 'completed':
			return 'text-green-600 bg-green-50 border-green-200';
		case 'failed':
			return 'text-red-600 bg-red-50 border-red-200';
		case 'processing':
			return 'text-blue-600 bg-blue-50 border-blue-200';
		case 'uploaded':
			return 'text-gray-600 bg-gray-50 border-gray-200';
		default:
			return 'text-yellow-600 bg-yellow-50 border-yellow-200';
	}
};

const formatDuration = (seconds?: number) => {
	if (seconds == null) return '-';

	if (seconds < 60) {
		return `${seconds.toFixed(1)}s`;
	}

	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;
	return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
};

const ProcessingLogs: React.FC<ProcessingLogsProps> = ({
	logsData,
	isLoading = false,
	error,
	onRetry,
}) => {
	const [retryStatement, { isLoading: isRetrying }] =
		useRetryStatementMutation();

	const handleRetry = async () => {
		if (!logsData?.file_id) return;

		try {
			await retryStatement(logsData.file_id).unwrap();
			// Call the onRetry callback if provided
			if (onRetry) {
				onRetry();
			}
		} catch (error) {
			console.error('Failed to retry statement:', error);
		}
	};

	// The legacy /retry action belongs to credit-settlement failures only;
	// every other failure recovers through the rerun flow in ProcessingStep.
	const shouldShowRetryButton = () => hasCreditSettlementFailure(logsData);

	const getStatusBadge = (status: string) => {
		if (status === 'failed') {
			return <span className="text-xs text-error-400">Failed</span>;
		}
		if (status === 'completed') {
			return <span className="text-xs text-gray-400">Completed</span>;
		}
		return null;
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64 w-full">
				<Loader size="lg" text="Loading processing logs..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center text-red-600">
					<XCircle className="w-12 h-12 mx-auto mb-4" />
					<p className="text-lg font-semibold">
						Failed to load processing logs
					</p>
					<p className="text-sm text-gray-500">
						Please try refreshing the page
					</p>
				</div>
			</div>
		);
	}

	if (!logsData || !logsData.logs.length) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center text-gray-500">
					<AlertCircle className="w-12 h-12 mx-auto mb-4" />
					<p className="text-lg font-semibold">No processing logs found</p>
					<p className="text-sm">
						Processing logs will appear here once processing begins
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white border-b border-gray-200  py-3 px-8">
			{/* Processing Steps List */}
			<div className="divide-y divide-gray-100">
				{/* Sort logs by created_at time */}
				{[...logsData.logs]
					.sort(
						(a, b) =>
							new Date(b.created_at).getTime() -
							new Date(a.created_at).getTime(),
					)
					.map((log: ProcessingLogEntry, index, sortedLogs) => (
						<div
							key={log.id}
							className="px-6 py-3 hover:bg-gray-50/50 transition-all duration-200"
						>
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1">
									{log.status === 'completed' && (
										<CheckCircleIcon
											weight="fill"
											size={16}
											className="text-green-500"
										/>
									)}
									{log.status === 'failed' && (
										<XCircleIcon size={16} className="text-red-500" />
									)}
									{log.status === 'processing' && (
										<ClockIcon size={16} className="text-blue-500" />
									)}
									<span className="text-sm text-gray-700">
										{log.step_name}{' '}
									</span>
									<span className="text-xs text-gray-400">
										{getStatusBadge(log.status)}
									</span>
									{log.status === 'failed' && (
										<span className="text-xs text-red-500">
											{log.error_message}
										</span>
									)}
								</div>
								<div className="flex items-center gap-1">
									<Clock className="w-3 h-3 text-green-500" />
									<span className="text-xs text-green-500">
										{log.duration_seconds
											? formatDuration(log.duration_seconds)
											: '0s'}
									</span>
								</div>
							</div>

							{/* Show error message if present */}
							{log.error_message && (
								<div className="mt-3 p-3 bg-red-50 border-l-4 border-red-400 rounded-lg">
									<div className="flex items-start">
										<XCircle className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
										<div>
											<h5 className="font-semibold text-red-800 mb-1 text-sm">
												Error Details
											</h5>
											<p className="text-xs text-red-700">
												{log.error_message}
											</p>
										</div>
									</div>
								</div>
							)}
						</div>
					))}
			</div>

			{/* Retry button section */}
			{shouldShowRetryButton() && (
				<div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
					<button
						onClick={handleRetry}
						disabled={isRetrying}
						className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
					>
						<RotateCcw className="w-4 h-4 mr-2" />
						{isRetrying ? 'Retrying...' : 'Retry'}
					</button>
				</div>
			)}
		</div>
	);
};

export default ProcessingLogs;
